import { getPageStatusLine } from "./page-context";

import { ActivityType, CourseType, PageType, TopicType } from "components/server-types";
import { t } from "i18n/i18n";
import {
  createAppSelector,
  selectActivities,
  selectCourseCodes,
  selectCourseOutcomes,
  selectCurrentCourse,
  selectOutcomes,
  selectPageOutcomes,
  selectPagesForCurrentCourse,
  selectTerms,
  selectTopicsForCurrentCourse,
} from "store/selectors";

export const TOPIC_EXPLANATION_CONTEXT =
  "A Topic is a thematic grouping of Activities within a course. Topics help organize course content into logical units and provide structure for the learning experience.";

export const ACTIVITY_EXPLANATION_CONTEXT =
  "An Activity is a collection of Pages that belong to a specific Topic. Activities can be categorized as prep, lesson, additional resources, or archive.";

export const COURSE_EXPLANATION_CONTEXT = t("ai.course_explanation", {
  returnObjects: true,
}).join("\n");

export const selectCourseMetadataContext = createAppSelector(
  [(state, course: CourseType) => course, selectTerms, selectCourseCodes, selectCurrentCourse],
  (course, terms, courseCodes, currentCourse) => {
    if (!course) {
      return "";
    }
    const term = terms.find((te) => te.id === course.term_id);
    const courseCode = courseCodes.find((cc) => cc.id === course.course_code_id);
    const courseCodeString = courseCode ? ` in ${courseCode.title}` : "";
    const termString = term ? ` in Term ${term.title}` : "";
    const courseDescription = course.description;
    const courseStatusLine = `Course "${course.title}"${courseCodeString}${termString}`;
    const currentCourseNote = currentCourse?.id === course.id ? ` (currently selected)` : "";
    return `${courseStatusLine}${currentCourseNote}, described as: "${courseDescription}"\n`;
  },
);

export const selectCourseContentSummaryContext = createAppSelector(
  [
    selectTopicsForCurrentCourse,
    selectActivities,
    (s) => selectPagesForCurrentCourse(s),
    selectOutcomes,
    selectCourseOutcomes,
    selectPageOutcomes,
  ],
  (topics, activities, pages, outcomes, courseOutcomes, pageOutcomes) => {
    const assignments = pages.filter((p) => p.category === "assignment");
    const courseResources = pages.filter((p) => p.category === "course_resource");
    const instructorWorkspace = pages.filter((p) => p.category === "instructor_workspace");

    const processedPages: PageType[] = [];
    const pageContextSelector = (_page: PageType) => {
      processedPages.push(_page);
      return `- ${getPageStatusLine(_page, pageOutcomes, courseOutcomes, outcomes)}`;
    };

    const selectTopicStructureContext = (topic: TopicType) => {
      const topicPages = pages.filter(
        (page) =>
          activities.find((activity) => activity.id === page.activity_id)?.topic_id === topic.id,
      );

      if (!topic) {
        return "Topic not found.";
      }

      // Group pages by activity
      const activitiesMap = new Map<string, { activity: ActivityType; pages: PageType[] }>();

      topicPages.forEach((page) => {
        const activity = activities.find((a) => a.id === page.activity_id);
        if (activity) {
          if (!activitiesMap.has(activity.id)) {
            activitiesMap.set(activity.id, { activity, pages: [] });
          }
          activitiesMap.get(activity.id)!.pages.push(page);
        }
      });

      // Sort activities by order
      const sortedActivities = Array.from(activitiesMap.values()).sort(
        (a, b) => a.activity.order - b.activity.order,
      );

      if (sortedActivities.length === 0) {
        return `Topic "${topic.title}" (contains no activities or pages)`;
      }

      const activityStructures = sortedActivities.map(({ activity, pages: activityPages }) => {
        const categoryLabels = {
          prep: "Prep",
          lesson: "Lesson",
          additional_resource: "Additional Resource",
          archive: "Archive",
        };

        const categoryLabel = categoryLabels[activity.category] || activity.category;
        const activityContext = `- ${activity.title} (${categoryLabel})`;

        if (activityPages.length === 0) {
          return activityContext;
        }

        // Sort pages by order
        const sortedPages = activityPages.sort((a, b) => a.order - b.order);
        const pageContexts = sortedPages.map((page) => `  ${pageContextSelector(page)}`);

        return [activityContext, ...pageContexts].join("\n");
      });

      return [`Topic "${topic.title}" with structure:`, ...activityStructures].join("\n");
    };

    const topicItems = topics.map((topic) => selectTopicStructureContext(topic)).join("\n");
    const assignmentItems = assignments.map((a) => pageContextSelector(a)).join("\n");
    const courseResourceItems = courseResources.map((r) => pageContextSelector(r)).join("\n");
    const instructorWorkspaceItems = instructorWorkspace
      .map((i) => pageContextSelector(i))
      .join("\n");
    const hasInstructorWorkspace = instructorWorkspaceItems.length > 0;

    return {
      pages: processedPages,
      context: [
        t("ai.course_content_summary"),
        t("ai.topics_list"),
        topicItems,
        t("ai.assignments_list"),
        assignmentItems,
        t("ai.course_resources_list"),
        courseResourceItems,
        hasInstructorWorkspace ? t("ai.instructor_workspace_list") : "",
        hasInstructorWorkspace ? instructorWorkspaceItems : "",
      ]
        .filter(Boolean)
        .join("\n"),
    };
  },
);
